import { RefObject, useRef, useState } from 'react'
import {
  Mail,
  CheckCircle2,
  PauseCircle,
  PlayCircle,
  XCircle,
  ChevronDown,
  PanelRight,
  Phone,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import WhatsAppBadge from '@/components/WhatsAppBadge'
import WhatsAppWindowIndicator from '@/components/WhatsAppWindowIndicator'
import SnoozePicker from '@/components/SnoozePicker'
import OnlineIndicator from './OnlineIndicator'
import { useInboxContext, type InboxContextValue } from './InboxContext'
import { getInitials, getAvatarColor, isUserOnline } from './utils'
import type { ConversationDetail } from './types'

export default function ChatHeader() {
  const {
    conversation,
    snoozePicker,
    snoozeButtonRef,
    onCloseConversation,
    onCloseWithRating,
    onReopenConversation,
    onUnsnoozeConversation,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
  } = useInboxContext()

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(
              conversation.end_user.name
            )}`}
          >
            {getInitials(conversation.end_user.name)}
          </div>
          {isUserOnline(conversation.end_user.last_seen_at) && <OnlineIndicator />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            {conversation.source === 'email' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Mail className="w-4 h-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent side="bottom">Conversa via e-mail</TooltipContent>
              </Tooltip>
            )}
            {conversation.source === 'whatsapp' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <WhatsAppBadge size="md" className="text-green-600" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">Conversa via WhatsApp</TooltipContent>
              </Tooltip>
            )}
            <h2 className="text-sm font-semibold text-gray-900">
              {conversation.end_user.name}
            </h2>
            {getStatusBadge(conversation.status)}
            {conversation.source === 'whatsapp' && (
              <WhatsAppWindowIndicator
                lastInboundAt={conversation.whatsapp_last_inbound_at}
              />
            )}
          </div>
          {conversation.source === 'whatsapp' && conversation.whatsapp_configuration && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              {conversation.whatsapp_configuration.name && (
                <>
                  <span className="text-gray-500 font-medium">{conversation.whatsapp_configuration.name}</span>
                  <span>·</span>
                </>
              )}
              <Phone className="w-3 h-3" />
              <span>{conversation.whatsapp_configuration.displayPhoneNumber}</span>
            </div>
          )}
        </div>
      </div>
      <HeaderActions
        conversation={conversation}
        snoozePicker={snoozePicker}
        snoozeButtonRef={snoozeButtonRef}
        onCloseConversation={onCloseConversation}
        onCloseWithRating={onCloseWithRating}
        onReopenConversation={onReopenConversation}
        onUnsnoozeConversation={onUnsnoozeConversation}
        isDetailsPanelOpen={isDetailsPanelOpen}
        onToggleDetailsPanel={onToggleDetailsPanel}
      />
    </header>
  )
}

function getStatusBadge(status: string): React.ReactNode {
  switch (status) {
    case 'open':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Aberta
        </span>
      )
    case 'closed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          <CheckCircle2 className="w-3 h-3" />
          Fechada
        </span>
      )
    case 'snoozed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <PauseCircle className="w-3 h-3" />
          Adiada
        </span>
      )
    default:
      return null
  }
}

interface HeaderActionsProps {
  conversation: ConversationDetail
  snoozePicker: InboxContextValue['snoozePicker']
  snoozeButtonRef: RefObject<HTMLButtonElement | null>
  onCloseConversation: () => void
  onCloseWithRating: () => void
  onReopenConversation: () => void
  onUnsnoozeConversation: () => void
  isDetailsPanelOpen: boolean
  onToggleDetailsPanel: () => void
}

function HeaderActions({
  conversation,
  snoozePicker,
  snoozeButtonRef,
  onCloseConversation,
  onCloseWithRating,
  onReopenConversation,
  onUnsnoozeConversation,
  isDetailsPanelOpen,
  onToggleDetailsPanel,
}: HeaderActionsProps) {
  const [isCloseDropdownOpen, setIsCloseDropdownOpen] = useState(false)
  const closeDropdownRef = useRef<HTMLDivElement>(null)
  const closeWithOptions = (
    <div className="relative" ref={closeDropdownRef}>
      <div className="inline-flex rounded-lg overflow-hidden">
        <button
          onClick={onCloseWithRating}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 cursor-pointer"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          Fechar
        </button>
        <button
          aria-label="Mais opções de fechamento"
          onClick={() => setIsCloseDropdownOpen(!isCloseDropdownOpen)}
          className="inline-flex items-center px-1.5 py-1 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 border-l border-emerald-500 cursor-pointer"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      {isCloseDropdownOpen && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <button
            onClick={() => {
              setIsCloseDropdownOpen(false)
              onCloseConversation()
            }}
            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
          >
            Fechar sem avaliação
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div className="flex items-center gap-2 relative">
      {conversation.status === 'open' && (
        <>
          <button
            ref={snoozeButtonRef}
            onClick={snoozePicker.open}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
          >
            <PauseCircle className="w-3.5 h-3.5" />
            Adiar
          </button>
          <SnoozePicker
            isOpen={snoozePicker.isOpen}
            mode={snoozePicker.mode}
            presetOptions={snoozePicker.presetOptions}
            selectedPresetIndex={snoozePicker.selectedPresetIndex}
            selectedDate={snoozePicker.selectedDate}
            selectedTimeIndex={snoozePicker.selectedTimeIndex}
            currentMonth={snoozePicker.currentMonth}
            timeSlots={snoozePicker.timeSlots}
            onSelectPreset={snoozePicker.selectPreset}
            onSelectDate={snoozePicker.selectDate}
            onSelectTime={snoozePicker.selectTime}
            onNavigateMonth={snoozePicker.navigateMonth}
            onSetMode={snoozePicker.setMode}
            onConfirmPreset={snoozePicker.confirmPreset}
            onConfirmCustom={snoozePicker.confirmCustom}
            onClose={snoozePicker.close}
            isLoading={snoozePicker.isLoading}
          />
          {closeWithOptions}
        </>
      )}
      {conversation.status === 'snoozed' && (
        <>
          <button
            onClick={onUnsnoozeConversation}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
          >
            <PlayCircle className="w-3.5 h-3.5" />
            Despausar
          </button>
          {closeWithOptions}
        </>
      )}
      {conversation.status === 'closed' && (
        <button
          onClick={onReopenConversation}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
        >
          <XCircle className="w-3.5 h-3.5" />
          Reabrir
        </button>
      )}
      {!isDetailsPanelOpen && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onToggleDetailsPanel}
              className="inline-flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer"
            >
              <PanelRight className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Mostrar detalhes</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
